import { Component, OnDestroy, OnInit } from '@angular/core';
import { AdminContactDepartment, AdminContactPerson } from '@app/model/contact.model';
import { ContactManagementDataService } from '@app/services/contact-management-data.service';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, finalize, take, takeUntil } from 'rxjs/operators';
import { MatDialog } from '@angular/material';
import { ContactPersonFormComponent } from '../contact-person-form/contact-person-form.component';
import { LayoutUtilsService } from '@app/core/_base/crud';
import { ToastrService } from 'ngx-toastr';
import { environment } from '@app/../environments/environment';

@Component({
  selector: 'app-contact-person-management',
  templateUrl: './contact-person-management.component.html',
  styleUrls: ['./contact-person-management.component.scss']
})
export class ContactPersonManagementComponent implements OnInit, OnDestroy {
  persons: AdminContactPerson[] = [];
  departments: AdminContactDepartment[] = [];

  searchText = '';
  statusFilter = 'all';
  departmentFilter = 'all';
  highlightFilter = 'all';

  pageIndex = 1;
  pageSize = 10;
  totalItems = 0;
  pageSizeOptions: number[] = [5, 10, 20, 100];

  isLoading = false;
  errorMessage = '';
  deletingPersonId = '';

  private destroy$ = new Subject<void>();
  private searchSubject = new Subject<string>();
  private personsSubscription = new Subscription();

  constructor(
    private contactManagementDataService: ContactManagementDataService,
    private dialog: MatDialog,
    private layoutUtilsService: LayoutUtilsService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.loadDepartments();

    // Debounce search 500ms then reload from the server.
    this.searchSubject
      .pipe(debounceTime(500), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        this.pageIndex = 1;
        this.loadPersons();
      });

    this.loadPersons();
  }

  ngOnDestroy(): void {
    this.personsSubscription.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
    this.searchSubject.complete();
  }

  getAvatarUrl(avatarPath: string | null): string {
    if (!avatarPath) {
      return '';
    }
    if (avatarPath.indexOf('http') === 0) {
      return avatarPath;
    }
    return environment.backendhost + avatarPath;
  }

  getDepartmentName(departmentId: string): string {
    const department = this.departments.find((d) => d.id === departmentId);
    return department ? department.name : '—';
  }

  loadDepartments(): void {
    this.contactManagementDataService
      .getDepartments({ sorts: 'sort_order', pageSize: 1000 })
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (departments) => {
          this.departments = departments || [];
        },
        (error) => {
          console.error('Load departments for filter failed:', error);
        }
      );
  }

  /** Build the Sieve filters string from search/status/department/highlight (AND via comma). */
  private buildFilters(): string {
    const conditions: string[] = [];
    const keyword = (this.searchText || '').trim();
    if (keyword) {
      // Per guide: departmentCode|name|position|phone|area@=keyword
      conditions.push(`departmentCode|name|position|phone|area@=${keyword}`);
    }
    if (this.departmentFilter !== 'all') {
      conditions.push(`departmentId==${this.departmentFilter}`);
    }
    if (this.statusFilter !== 'all') {
      conditions.push(`isActive==${this.statusFilter === 'active' ? 'true' : 'false'}`);
    }
    if (this.highlightFilter !== 'all') {
      conditions.push(`isHighlight==${this.highlightFilter === 'yes' ? 'true' : 'false'}`);
    }
    return conditions.join(',');
  }

  loadPersons(): void {
    this.isLoading = true;
    this.errorMessage = '';

    this.personsSubscription.unsubscribe();
    this.personsSubscription = this.contactManagementDataService
      .getPersonsPaged({
        filters: this.buildFilters(),
        sorts: 'sort_order',
        page: this.pageIndex,
        pageSize: this.pageSize
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (result) => {
          this.persons = (result && result.collection) || [];
          this.totalItems = (result && result.total) || 0;
          this.isLoading = false;
        },
        (error) => {
          console.error('Load contact persons failed:', error);
          this.persons = [];
          this.totalItems = 0;
          this.isLoading = false;
          this.errorMessage = 'Không thể tải danh sách nhân sự';
        }
      );
  }

  onSearchChange(value: string): void {
    this.searchText = value;
    this.searchSubject.next(value);
  }

  onDepartmentFilterChange(): void {
    this.pageIndex = 1;
    this.loadPersons();
  }

  onStatusFilterChange(): void {
    this.pageIndex = 1;
    this.loadPersons();
  }

  onHighlightFilterChange(): void {
    this.pageIndex = 1;
    this.loadPersons();
  }

  clearFilters(): void {
    this.searchText = '';
    this.statusFilter = 'all';
    this.departmentFilter = 'all';
    this.highlightFilter = 'all';
    this.pageIndex = 1;
    this.loadPersons();
  }

  onPageChange(pageNumber: number): void {
    if (typeof pageNumber !== 'number') {
      return;
    }
    this.pageIndex = pageNumber;
    this.loadPersons();
    window.scrollTo(0, 0);
  }

  onPageSizeChange(newSize: number): void {
    this.pageSize = newSize;
    this.pageIndex = 1;
    this.loadPersons();
  }

  openCreatePerson(): void {
    if (!this.departments.length) {
      this.toastr.error('Vui lòng tạo phòng ban trước khi thêm nhân sự');
      return;
    }
    const dialogRef = this.dialog.open(ContactPersonFormComponent, {
      width: '960px',
      maxWidth: 'calc(100vw - 32px)',
      maxHeight: '90vh',
      autoFocus: false,
      disableClose: true,
      panelClass: 'contact-person-form-dialog',
      data: { mode: 'create', departments: this.departments }
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result && result.created) {
          this.loadPersons();
        }
      });
  }

  openEditPerson(person: AdminContactPerson): void {
    const dialogRef = this.dialog.open(ContactPersonFormComponent, {
      width: '960px',
      maxWidth: 'calc(100vw - 32px)',
      maxHeight: '90vh',
      autoFocus: false,
      disableClose: true,
      panelClass: 'contact-person-form-dialog',
      data: { mode: 'edit', person, departments: this.departments }
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result && result.updated) {
          this.loadPersons();
        }
      });
  }

  confirmDeletePerson(person: AdminContactPerson): void {
    const title = 'Xóa nhân sự';
    const description = 'Bạn có chắc chắn muốn xóa nhân sự "' + (person.full_name || '') + '" không?';
    const waitDescription = 'Đang xóa nhân sự...';

    const dialogRef = this.layoutUtilsService.deleteElement(title, description, waitDescription);

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((confirmed) => {
        if (!confirmed) {
          return;
        }
        this.deletePerson(person);
      });
  }

  private deletePerson(person: AdminContactPerson): void {
    this.deletingPersonId = person.id;

    this.contactManagementDataService
      .deletePerson(person.id)
      .pipe(
        take(1),
        finalize(() => {
          this.deletingPersonId = '';
        })
      )
      .subscribe(
        (response) => {
          if (!response || !response.success) {
            this.toastr.error(response && response.message ? response.message : 'Không thể xóa nhân sự');
            return;
          }
          this.toastr.success('Xóa nhân sự thành công');
          this.loadPersons();
        },
        (error) => {
          const errorResponse = error && error.error ? error.error : null;
          let message = 'Không thể xóa nhân sự';
          if (errorResponse && errorResponse.message) {
            message = errorResponse.message;
          } else if (errorResponse && errorResponse.title) {
            message = errorResponse.title;
          }
          this.toastr.error(message);
        }
      );
  }
}
